import React, { useEffect } from 'react'
import { HashRouter as Router } from 'react-router-dom'
import { Welcome, LanguagePicker, SafetyRules, Quiz, QRCode } from './Pages'
import { Navbar } from './components'
import { LanguageProvider, useLanguage } from './context/LanguageContext'
import DynamicDataEntry from './Pages/dataEntry/DynamicDataEntry'
import InfoPage from './Pages/infoPage/InfoPage'
import './App.css'

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'

// ── Page component registry ───────────────────────────────────────────────────
// Add new page types here as they are built. The flow builder and API need no
// changes — just register the component and build it.
const PAGE_COMPONENTS = {
  language_picker: LanguagePicker,
  welcome:         Welcome,
  data_entry:      DynamicDataEntry,
  safety_rules:    SafetyRules,
  quiz:            Quiz,
  confirmation:    QRCode,
  info_page:       InfoPage,
}

// ── Translation loader ────────────────────────────────────────────────────────
const TranslationLoader = () => {
  const { tenantSlug, setAllTranslations } = useLanguage()

  useEffect(() => {
    if (!tenantSlug) return
    fetch(`${BACKEND}/api/checkin/translations`, {
      headers: { 'X-Tenant-Slug': tenantSlug },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAllTranslations(data) })
      .catch(() => {}) // silently keep bundled defaults
  }, [tenantSlug])

  return null
}

// ── Tenant + flow config resolver ─────────────────────────────────────────────
const TenantResolver = () => {
  const { setTenantSlug, setFlowSteps } = useLanguage()

  const resolveWithSlug = (slug) => {
    setTenantSlug(slug)
    sessionStorage.setItem('viGuard_tenant', slug)
    fetch(`${BACKEND}/api/checkin/form-config?flowType=pre_checkin`, {
      headers: { 'X-Tenant-Slug': slug },
    })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data.steps)) setFlowSteps(data.steps) })
      .catch(() => {}) // silently fall back to defaults in context
  }

  useEffect(() => {
    // 1. Subdomain: tenant.viguard.net
    const parts = window.location.hostname.split('.')
    if (parts.length > 2 && parts[0] !== 'www') {
      resolveWithSlug(parts[0])
      return
    }
    // 2. Query string: ?tenant=slug
    const searchParams = new URLSearchParams(window.location.search)
    const fromSearch = searchParams.get('tenant')
    if (fromSearch) { resolveWithSlug(fromSearch); return }
    // 3. Hash query: #/?tenant=slug
    const hash = window.location.hash
    const qi = hash.indexOf('?')
    if (qi !== -1) {
      const fromHash = new URLSearchParams(hash.slice(qi)).get('tenant')
      if (fromHash) { resolveWithSlug(fromHash); return }
    }
    // 4. Session storage
    const saved = sessionStorage.getItem('viGuard_tenant')
    if (saved) { resolveWithSlug(saved); return }
    console.warn('No tenant slug found. Add ?tenant=yourslug to the URL.')
  }, [])

  return null
}

// ── Dynamic flow renderer ─────────────────────────────────────────────────────
const FlowRenderer = () => {
  const { flowSteps, currentStepIndex, goNext } = useLanguage()
  const step = flowSteps[currentStepIndex]
  const Component = step ? PAGE_COMPONENTS[step.type] : null

  // Unknown type (future page not yet built) — skip automatically
  useEffect(() => {
    if (step && !Component) goNext()
  }, [step, Component, goNext])

  if (!step || !Component) return null

  return <Component stepConfig={step.config ?? {}} />
}

// ── App ───────────────────────────────────────────────────────────────────────
const App = () => (
  <LanguageProvider>
    <Router>
      <TenantResolver />
      <TranslationLoader />
      <div className="App">
        <Navbar />
        <FlowRenderer />
      </div>
    </Router>
  </LanguageProvider>
)

export default App
